import {
  Grid,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Typography,
  useMediaQuery,
  Alert,
  SelectChangeEvent,
  Link,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { Box } from "@mui/system";
import PhoneInput from "react-phone-input-2";
import { TIMEZONE, DATE_FORMAT } from "../../../../config/helper";
import {
  OUTLET_ORGANISATIONS,
  OUTLETS,
  setCreateBookingForm,
  typeBookingForm,
} from "../../HomeSlice";
import momentTz, { Moment } from "moment-timezone";
import { blue } from "@mui/material/colors";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import logo from "../../../../assets/images/havefun-logo.png";
import { useAppDispatch } from "../../../../config/hooks";
import { AvailabilitySlot } from "../../utils/generateStartTimes";
import { sortObjWithIsoStringKeyAsc } from "../../utils/sortObjWithIsoStringKeyAsc";
import React, { useState } from "react";
import { createAppointment, typeCustomKey } from "../../HomeApi";
import PhoneNumber from "libphonenumber-js";
import { setIsLoading } from "../../../../AppSlice";
import { useHistory } from "react-router-dom";
import { getPublicHolidaysWithEves } from "../../utils/getPublicHolidaysWithEve";

interface BookingFormProps {
  customKeys: typeCustomKey[];
  form: typeBookingForm;
  availableRoomTypes: string[];
  availableTimeslots: { [key: string]: AvailabilitySlot[] };
  isLoadingTimeslots: boolean;
  minimumNoticeInDays: number;
  dateRangeInDays: number;
}

interface ErrorForm {
  outlet: string;
  pax: string;
  roomType: string;
  date: string;
  duration: string;
  startTime: string;
  phone: string;
  name: string;
}

const BookingForm = ({
  customKeys,
  form,
  availableRoomTypes,
  availableTimeslots,
  minimumNoticeInDays,
  dateRangeInDays,
}: BookingFormProps) => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const isMobile = useMediaQuery(`(max-width:600px)`);
  const [errors, setErrors] = useState<ErrorForm>({
    outlet: "",
    pax: "",
    roomType: "",
    date: "",
    duration: "",
    startTime: "",
    phone: "",
    name: "",
  });

  const [checkboxError, setCheckboxError] = useState<boolean>(false);
  const [lateArrivalAcknowledged, setLateArrivalAcknowledged] =
    useState<boolean>(false);

  // Check if there are no available room types for the selected pax
  const hasNoRoomTypes = form.pax > 0 && availableRoomTypes.length === 0;

  function shouldDisableDate(date: Moment | null): boolean {
    if (!date) return false;

    // Disable date picker dates for eves of ph and ph
    const holidayWithEves = getPublicHolidaysWithEves();
    const formattedHolidayWithEves = holidayWithEves.map((d) =>
      d.format(DATE_FORMAT)
    );

    const formattedDate = date.tz(TIMEZONE).format(DATE_FORMAT);

    if (formattedHolidayWithEves.includes(formattedDate)) {
      return true;
    }

    // Disable date picker dates for friday and saturday
    const day = date.day(); // Get the day of the week (0 is Sunday, 6 is Saturday)
    if (day === 5 || day === 6) {
      return true; // Disable Friday (5), and Saturday (6)
    }

    const today = momentTz().startOf("day");
    const maxDate = momentTz().add(dateRangeInDays, "days").endOf("day");

    const minimumNotice = momentTz()
      .add(minimumNoticeInDays, "days")
      .startOf("day");

    if (
      date.isBefore(today) ||
      date.isAfter(maxDate) ||
      date.isBefore(minimumNotice)
    ) {
      return true; // Disable dates before today, after 3 weeks from now, or within the minimum scheduling notice
    }

    return false;
  }

  const sortedAvailableTimeslots =
    sortObjWithIsoStringKeyAsc(availableTimeslots);

  const handleChangeOutlet = (value: (typeof OUTLETS)[number]) => {
    resetErrors();
    dispatch(
      setCreateBookingForm({
        ...form,
        outlet: value,
      })
    );
  };

  const handleChangePax = (event: SelectChangeEvent) => {
    resetErrors();
    dispatch(
      setCreateBookingForm({
        ...form,
        pax: parseInt(event.target.value),
      })
    );
  };

  const handleChangeRoomType = (event: SelectChangeEvent) => {
    resetErrors();
    dispatch(
      setCreateBookingForm({
        ...form,
        roomType: event.target.value,
      })
    );
  };

  const handleChangeDate = (value: momentTz.Moment | null) => {
    resetErrors();

    try {
      if (value?.toDate()) {
        dispatch(
          setCreateBookingForm({
            ...form,
            date: value ? value.toDate() : null,
          })
        );
      }
    } catch (error) {
      console.log("Invalid date: ", value);
      dispatch(
        setCreateBookingForm({
          ...form,
          date: null,
        })
      );
      return;
    }
  };

  const handleChangeDuration = (event: SelectChangeEvent) => {
    resetErrors();
    dispatch(
      setCreateBookingForm({
        ...form,
        duration: parseInt(event.target.value),
      })
    );
  };

  const handleChangeStartTime = (event: SelectChangeEvent) => {
    resetErrors();
    dispatch(
      setCreateBookingForm({
        ...form,
        dateTime: event.target.value,
      })
    );
  };

  const handleChangePhone = (value: string) => {
    resetErrors();
    dispatch(
      setCreateBookingForm({
        ...form,
        phone: value,
      })
    );
  };

  const handleChangeName = (event: React.ChangeEvent<HTMLInputElement>) => {
    resetErrors();
    dispatch(
      setCreateBookingForm({
        ...form,
        name: event.target.value,
      })
    );
  };

  const resetErrors = () => {
    setErrors({
      outlet: "",
      pax: "",
      roomType: "",
      date: "",
      duration: "",
      startTime: "",
      phone: "",
      name: "",
    });
  };

  const validate = () => {
    const errorsTemp: ErrorForm = {
      outlet: "",
      pax: "",
      roomType: "",
      date: "",
      duration: "",
      startTime: "",
      phone: "",
      name: "",
    };

    let errorExist = false;
    if (!!!form.date) {
      errorsTemp.date = "Date is required.";
      errorExist = true;
    }

    if (!!!form.dateTime) {
      errorsTemp.startTime = "Time is required.";
      errorExist = true;
    } else if (shouldDisableDate(momentTz(form.dateTime).tz(TIMEZONE))) {
      errorsTemp.date =
        "Please choose another date as online booking is not allowed on blocked off dates";
      errorExist = true;
    }

    if (form.phone.length <= 6) {
      errorsTemp.phone = "Phone number is required.";
      errorExist = true;
    }
    if (form.name.length === 0) {
      errorsTemp.name = "Name is required.";
      errorExist = true;
    }

    if (form.outlet.length === 0) {
      errorsTemp.outlet = "Outlet is required.";
      errorExist = true;
    }

    if (form.pax.toString().length === 0) {
      errorsTemp.pax = "Number of pax is required.";
      errorExist = true;
    }

    if (form.roomType.length === 0) {
      errorsTemp.roomType = "Room type is required.";
      errorExist = true;
    }

    if (!lateArrivalAcknowledged) {
      setCheckboxError(true);
      errorExist = true;
    } else {
      setCheckboxError(false);
    }

    setErrors(errorsTemp);

    // console.log("errorsTemp: ", errorsTemp);
    return !errorExist;
  };

  const handleClickBook = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const valid = validate();

    if (!valid) {
      return;
    }

    const startTime = momentTz(form.dateTime).tz(TIMEZONE).toDate();
    const end = momentTz(form.dateTime).tz(TIMEZONE);
    end.set({ hour: end.hour() + form.duration });
    const endTime = end.toDate();
    const phone = PhoneNumber(`+${form.phone}`);

    const payload = {
      outlet: form.outlet,
      createAppointmentForm: {
        calendarUuid: availableTimeslots[form.dateTime][0].calendarUuid,
        name: form.name,
        phone: form.phone,
        countryCode: phone?.countryCallingCode ?? "",
        nationalNumber: phone?.nationalNumber ?? "",
        title: `[ONLINE BOOKING] ${form.name} / ${form.phone} / ${form.pax} pax`,
        color: "#fff176",
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
        customFields: [
          {
            customKeyUuid:
              customKeys.find(({ name }) => name === "Status")?.uuid ?? "",
            value: "Unconfirmed",
          },
          {
            customKeyUuid:
              customKeys.find(({ name }) => name === "Pax Number")?.uuid ?? "",
            value: form.pax.toString(),
          },
        ],
      },
    };

    // console.log("handleBookOnClick: payload: ", payload);
    dispatch(createAppointment(payload));
    dispatch(setIsLoading(true));
    history.push("/confirmation");
  };

  const getErrorMessages = () => {
    const errorMessages = [];
    if (errors.date) errorMessages.push(errors.date);
    if (errors.duration) errorMessages.push(errors.duration);
    if (errors.name) errorMessages.push(errors.name);
    if (errors.outlet) errorMessages.push(errors.outlet);
    if (errors.pax) errorMessages.push(errors.pax);
    if (errors.phone) errorMessages.push(errors.phone);
    if (errors.roomType) errorMessages.push(errors.roomType);
    if (errors.startTime) errorMessages.push(errors.startTime);
    if (checkboxError)
      errorMessages.push("You must acknowledge the late arrival policy.");

    return errorMessages;
  };

  const errorMessages = getErrorMessages();

  return (
    <Box
      component="form"
      onSubmit={handleClickBook}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        maxWidth: "600px",
        maxHeight: isMobile ? "auto" : "92vh",
        overflowY: isMobile ? "auto" : "scroll",
        width: "100%",
        background: "#FAFAFA",
        padding: 2,
        paddingBottom: isMobile ? 16 : 4,
        borderRadius: "4px",
        boxSizing: "border-box",
      }}
    >
      <Grid
        component="form"
        container
        direction="column"
        textAlign="start"
        spacing={2}
      >
        <Grid item>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img alt="HaveFun Logo" src={logo} width={110} />
          </div>
          <div style={{ marginTop: "18px" }}></div>
          <Box textAlign="center">
            <Typography variant="body1" fontWeight="700">
              Make a booking
            </Typography>
          </Box>
          <Box paddingTop={2} paddingBottom={2}>
            {/* <Alert icon={false} severity="info">
              We are currently conducting a trial of this service. For room
              bookings on Fridays, Saturdays, and Sundays, please contact our
              outlets directly. We apologize for any inconvenience.
            </Alert> */}

            {errorMessages.length > 0 ? (
              <>
                <div style={{ marginTop: "18px" }}></div>
                <Alert icon={false} severity="error">
                  {errorMessages.map((message, index) => (
                    <React.Fragment key={index}>
                      {index > 0 && <br />}{" "}
                      {/* Add <br> after the first message */}
                      {"- " + message}
                    </React.Fragment>
                  ))}
                </Alert>
              </>
            ) : (
              ""
            )}
          </Box>
        </Grid>
        <Grid item>
          <InputLabel sx={{ marginBottom: "8px" }}>Outlet</InputLabel>
          <Select
            error={errors.outlet.length > 0}
            required
            fullWidth
            name="outlet"
            value={form.outlet}
            onChange={(e) =>
              handleChangeOutlet(e.target.value as (typeof OUTLETS)[number])
            }
          >
            {OUTLETS.map((outlet) => (
              <MenuItem key={outlet} value={outlet}>
                {outlet}
              </MenuItem>
            ))}
          </Select>
          <Box
            sx={{
              paddingTop: "8px",
              paddingBottom: "8px",
              display: "flex",
              columnGap: "4px",
              color: blue[800],
            }}
          >
            <Link
              href={`${OUTLET_ORGANISATIONS[form.outlet].menuRate}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Click here to view rate & menu for {form.outlet}
            </Link>
          </Box>
        </Grid>
        <Grid item>
          <InputLabel sx={{ marginBottom: "8px" }}>No. of Pax</InputLabel>
          <Select
            error={errors.pax.length > 0}
            required
            fullWidth
            name="pax"
            value={form.pax.toString()}
            onChange={handleChangePax}
          >
            {Array.from({ length: 40 }, (_, i) => i + 1).map((paxOption) => (
              <MenuItem key={paxOption} value={paxOption}>
                {paxOption}
              </MenuItem>
            ))}
          </Select>
        </Grid>

        {/* Show message when no room types are available */}
        {hasNoRoomTypes && (
          <Grid item>
            <Alert
              icon={false}
              severity="warning"
              sx={{
                backgroundColor: "#fff3cd",
                color: "#856404",
                border: "1px solid #ffeaa7",
              }}
            >
              <Typography variant="body2" fontWeight="600">
                No room types available for your selected group size at selected
                outlet.
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                For larger group bookings, please WhatsApp us at{" "}
                <Typography
                  component="a"
                  href="https://api.whatsapp.com/send/?phone=6589091123&text&type=phone_number&app_absent=0"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: "#25D366", // WhatsApp green color
                    fontWeight: 600,
                    textDecoration: "underline",
                    cursor: "pointer",
                    "&:hover": {
                      color: "#128C7E", // Darker WhatsApp green on hover
                    },
                  }}
                >
                  8909 1123
                </Typography>{" "}
                for VIP Room arrangement.
              </Typography>
            </Alert>
          </Grid>
        )}

        {/* Only show remaining fields if room types are available */}
        {!hasNoRoomTypes && (
          <>
            <Grid item>
              <InputLabel sx={{ marginBottom: "8px" }}>Room Type</InputLabel>
              <Select
                error={errors.roomType.length > 0}
                required
                fullWidth
                name="roomType"
                value={form.roomType}
                onChange={handleChangeRoomType}
              >
                {availableRoomTypes.map((roomType) => (
                  <MenuItem key={roomType} value={roomType}>
                    {roomType}
                  </MenuItem>
                ))}
              </Select>
              <Typography
                sx={{
                  fontSize: 12,
                  color: blue[800],
                  mt: 1,
                  textAlign: "left",
                }}
              >
                Available room types are determined by the number of pax
                specified
              </Typography>
            </Grid>
            <Grid item>
              <InputLabel sx={{ marginBottom: "8px" }}>Date</InputLabel>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  value={form.date ? momentTz(form.date) : form.date}
                  onChange={handleChangeDate}
                  format="DD/MM/YYYY"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: errors.date.length > 1,
                    },
                  }}
                  shouldDisableDate={shouldDisableDate}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item>
              <InputLabel sx={{ marginBottom: "8px" }}>Duration</InputLabel>
              <Select
                required
                fullWidth
                name="duration"
                value={form.duration.toString()}
                onChange={handleChangeDuration}
              >
                {Array.from({ length: 5 }, (_, i) => i + 2).map(
                  (durationHour) => (
                    <MenuItem key={durationHour} value={durationHour}>
                      {durationHour} hour{durationHour > 1 ? "s" : ""}
                    </MenuItem>
                  )
                )}
              </Select>
            </Grid>
            <Grid item>
              <InputLabel htmlFor="startTime" sx={{ marginBottom: "8px" }}>
                Start Time
              </InputLabel>
              <Select
                error={errors.startTime.length > 0}
                required
                fullWidth
                name="startTime"
                value={form.dateTime}
                onChange={handleChangeStartTime}
              >
                {Object.keys(sortedAvailableTimeslots).map((key) => (
                  <MenuItem key={key} value={key}>
                    {momentTz(key).tz("Asia/Singapore").format("hh:mm A")}
                  </MenuItem>
                ))}
              </Select>
              <Typography
                sx={{
                  fontSize: 12,
                  color: blue[800],
                  mt: 1,
                  textAlign: "left",
                }}
              >
                If the desired time slot is unavailable, you can change the
                selected outlet or room type above.
              </Typography>
            </Grid>
            <Grid item>
              <InputLabel sx={{ marginBottom: "8px" }}>Phone</InputLabel>
              <PhoneInput
                value={form.phone}
                onChange={handleChangePhone}
                country={"sg"}
                containerStyle={{
                  border: errors.phone.length > 1 ? "1px solid red" : "none",
                  borderRadius: "4px",
                }}
                inputStyle={{
                  border: "1px solid #C0C0C0",
                  backgroundColor: "#FAFAFA",
                  width: "100%",
                  height: "56px",
                }}
                buttonStyle={{
                  height: "56px",
                }}
              />
            </Grid>
            <Grid item>
              <InputLabel sx={{ marginBottom: "8px" }}>Name</InputLabel>
              <TextField
                error={errors.name.length > 0}
                required
                name="name"
                value={form.name}
                onChange={handleChangeName}
                fullWidth
              />
            </Grid>
          </>
        )}
      </Grid>

      {/* Only show the Book button if room types are available */}
      {!hasNoRoomTypes && (
        <>
          <Box
            sx={{
              border: checkboxError ? "2px solid #d32f2f" : "2px solid #e0e0e0",
              borderRadius: 1,
              padding: 2,
              mt: 2,
              mb: 2,
              backgroundColor: checkboxError ? "#ffebee" : "#fafafa",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={lateArrivalAcknowledged}
                  onChange={(e) => {
                    setLateArrivalAcknowledged(e.target.checked);
                    setCheckboxError(false);
                  }}
                  sx={{
                    color: checkboxError ? "error.main" : "inherit",
                    alignSelf: "flex-start",
                  }}
                />
              }
              label={
                <Typography
                  variant="body2"
                  sx={{
                    color: checkboxError ? "error.main" : "text.primary",
                    fontWeight: 500,
                    textAlign: "start",
                  }}
                >
                  <span style={{ color: "#d32f2f" }}>* </span>I acknowledge that
                  if I arrive more than 15 minutes late without prior notice, my
                  reservation may be released to other guests.
                </Typography>
              }
              sx={{ alignItems: "flex-start", ml: 0 }}
            />
          </Box>

          <Button
            type="submit"
            variant="contained"
            sx={{ padding: 2, fontWeight: 700 }}
          >
            Book
          </Button>
          {/* <Button
            type="submit"
            variant="contained"
            sx={{ padding: 2, fontWeight: 700 }}
          >
            Book
          </Button>

          <FormControlLabel
            control={
              <Checkbox
                checked={lateArrivalAcknowledged}
                onChange={(e) => {
                  setLateArrivalAcknowledged(e.target.checked);
                  setCheckboxError(false);
                }}
                sx={{
                  color: checkboxError ? "error.main" : "inherit",
                }}
              />
            }
            label={
              <Typography
                variant="body2"
                sx={{
                  color: checkboxError ? "error.main" : "text.secondary",
                  textAlign: "start",
                }}
              >
                I acknowledge that if I arrive more than 15 minutes late without
                prior notice, my reservation may be released to other guests.
              </Typography>
            }
          /> */}
        </>
      )}
    </Box>
  );
};

export default BookingForm;
